import { PayablePoolToken } from '@ui/types/token';
import { BareTradePair } from '@ui/types/tradePair';
import { Nullable } from '@ui/types/utility';
import BigNumber from 'bignumber.js';

import {
  ClosePositionDetailExecutionTypeEnum,
  TradeHistoryResponseOperationTypeEnum,
  TradeHistoryResponseTradeTypeEnum,
} from '../../generated';

export enum HistoryStatus {
  CREATED = 'CREATED',
  EXECUTED = 'EXECUTED',
  CANCELLED = 'CANCELLED',
  UPDATED = 'UPDATED',
}

export type TradeHistoryResponse = {
  page: number;
  size: number;
  totalPages?: number;
  histories: TradeHistory[];
};

interface BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum;
  status: HistoryStatus;
  detail: object | null;

  id: string;
  pair: BareTradePair;
  size: BigNumber;
  entryPrice: Nullable<BigNumber>;
  /**
   * Identifier used to visually group events that belong to the same position.
   * positionHash when the event ties to a position; null otherwise (e.g. limit
   * order lifecycle events, until BE exposes orderHash on those records).
   */
  groupHash: Nullable<string>;
  getSizeUsd: (pairPrice: Nullable<BigNumber>) => Nullable<BigNumber>;
  tradeType: TradeHistoryResponseTradeTypeEnum;
  blockTime: Date;
  blockNumber: number;
  isLong: boolean;

  getLeverage: (params: {
    pairPrice: Nullable<BigNumber>;
    collateralPrice: Nullable<BigNumber>;
    openFeeP: Nullable<number>;
    lvTokenDiscountP: Nullable<number>;
    executionFeeUsd: Nullable<BigNumber>;
  }) => Nullable<BigNumber>;

  transactionHash: string;
  explorer: string;
  transactionIndex: number;
  logIndex: number;
  tokenIn: PayablePoolToken;
  lvToken: PayablePoolToken;
  amountIn: BigNumber;

  /**
   * nullable coz BE not sure if can get price snapshot
   */
  tokenInPrice: Nullable<BigNumber>;
  amountInUsd: Nullable<BigNumber>;
  /**
   * this is the LIVE collateral of this position, not to confuse with historical margin in detail.margin
   */
  collateral: Nullable<BigNumber>;
  getCollateralUsd: ({
    pairPrice,
    collateralPrice,
    openFeeP,
    lvTokenDiscountP,
    executionFeeUsd,
  }: {
    pairPrice: Nullable<BigNumber>;
    collateralPrice: Nullable<BigNumber>;
    openFeeP: Nullable<number>;
    lvTokenDiscountP: Nullable<number>;
    executionFeeUsd: Nullable<BigNumber>;
  }) => Nullable<BigNumber>;
}

export type TradeHistory =
  | OpenLimitOrder
  | UserCloseTradeSuccessful
  | UpdateOrderSl
  | UpdateOrderTp
  | LimitOrderRefund
  | OpenMarketTrade
  | OpenPosition
  | PositionIncreased
  | PendingTradeRefund
  | ExecuteCloseSuccessful
  | ExecuteDecreaseOrderSuccessful
  | DecreaseOrderCreated
  | DecreaseOrderCancelled
  | DecreaseOrderUpdated
  | ClosePosition
  | PositionDecreased
  | UserCancelLimitOrder
  | UpdatePositionMargin
  | UpdatePositionTp
  | UpdatePositionSl
  | MarketPendingTrade
  | ExecuteLimitOrderRejected
  | ExecuteLimitOrderSuccessful
  | ExecuteCloseRejected;

interface OpenLimitOrder extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.OpenLimitOrder;
  status: HistoryStatus.CREATED;
  detail: {
    /**
     * ignore it
     */
    amountIn: BigNumber;
    /**
     * trigger price
     */
    price: BigNumber;
    takeProfit: BigNumber;
    stopLoss: BigNumber;
  };
}

/**
 * ignore this event in UI, if limit order executed successfully to create new position,
 * will emit a OpenMarketTrade event with identifier from limit order
 * @deprecated
 */
interface ExecuteLimitOrderSuccessful extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.ExecuteLimitOrderSuccessful;
  detail: null;
}

/**
 * ignore this event in UI, this is when keeper failed to execute limit order,
 * keeper will try again til success
 * for 'real' failure, see LimitOrderRefund
 * @deprecated
 */
interface ExecuteLimitOrderRejected extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.ExecuteLimitOrderRejected;
  detail: null;
}
interface UpdateOrderSl extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.UpdateOrderSl;
  status: HistoryStatus.UPDATED;
  detail: {
    oldSl: BigNumber;
    sl: BigNumber;
  };
}
interface UpdateOrderTp extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.UpdateOrderTp;
  status: HistoryStatus.UPDATED;
  detail: {
    oldTp: BigNumber;
    tp: BigNumber;
  };
}
/**
 * user manually cancelled a limit order
 */
interface UserCancelLimitOrder extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.CancelLimitOrder;
  status: HistoryStatus.EXECUTED;
  detail: null;
}
/**
 * when refunded, means
 * - limit order execution is officially cancelled
 * - removed from contract store
 * - keeper will not try execute this order again
 */
interface LimitOrderRefund extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.LimitOrderRefund;
  status: HistoryStatus.CANCELLED;
  detail: {
    // reason: after mvp
    refund: string;
  };
}
interface OpenMarketTrade extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.OpenMarketTrade;
  status: HistoryStatus.EXECUTED;
  collateral: BigNumber;
  detail: {
    collateral: BigNumber;
    /**
     * in collateral token amount
     */
    executionFee: BigNumber;
    positionSize: BigNumber;
    atPrice: BigNumber;
  };
}
interface OpenPosition extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.OpenPosition;
  status: HistoryStatus.EXECUTED;
  collateral: BigNumber;
  detail: OpenMarketTrade['detail'];
}
interface PositionIncreased extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.PositionIncreased;
  status: HistoryStatus.EXECUTED;
  collateral: BigNumber;
  detail: {
    collateral: BigNumber;
    /**
     * in collateral token amount
     */
    executionFee: BigNumber;
    positionSize: BigNumber;
    atPrice: BigNumber;
  };
}
interface UpdatePositionMargin extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.UpdateMargin;
  status: HistoryStatus.UPDATED;
  collateral: BigNumber;
  detail: {
    previousCollateral: BigNumber;
    nextCollateral: BigNumber;
  };
}
interface UpdatePositionTp extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.UpdateTradeTp;
  status: HistoryStatus.UPDATED;
  collateral: BigNumber;
  detail: {
    oldTp: BigNumber;
    tp: BigNumber;
  };
}
interface UpdatePositionSl extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.UpdateTradeSl;
  status: HistoryStatus.UPDATED;
  collateral: BigNumber;
  detail: {
    oldSl: BigNumber;
    sl: BigNumber;
  };
}
/**
 * when market order creation failed
 */
interface PendingTradeRefund extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.PendingTradeRefund;
  status: HistoryStatus.CANCELLED;
  detail: {
    // reason: after mvp
    refund: string;
  };
}
/**
 * when user created market order
 */
interface MarketPendingTrade extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.MarketPendingTrade;
  status: HistoryStatus.CREATED;
  detail: {
    amountIn: BigNumber;
    price: BigNumber;
    stopLoss: BigNumber;
    takeProfit: BigNumber;
    broker: string;
  };
}

/**
 * UI can ignore it, it is emitted when keeper failed trying to close position by (tp/sl/liq)
 * @deprecated
 */
interface ExecuteCloseRejected extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.ExecuteCloseRejected;
  status: HistoryStatus.CANCELLED;
  collateral: BigNumber;
  detail: null;
}

/**
 * keeper close position event, caused by tp/sl/liq
 */
export interface ExecuteCloseSuccessful extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful;
  status: HistoryStatus.EXECUTED;
  collateral: BigNumber;
  detail: {
    executionType?: ClosePositionDetailExecutionTypeEnum;
    closePrice: BigNumber;
    fundingFee: BigNumber;
    closeFee: BigNumber;
    holdingFee: BigNumber;
    pnl: BigNumber;
    pnlAfterFee: BigNumber;
    getPnlUsd: (
      collateralTokenPrice: Nullable<BigNumber>,
    ) => Nullable<BigNumber>;
    getPnlAfterFeeUsd: (
      collateralTokenPrice: Nullable<BigNumber>,
    ) => Nullable<BigNumber>;
  };
}
export interface ExecuteDecreaseOrderSuccessful extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful;
  status: HistoryStatus.EXECUTED;
  collateral: BigNumber;
  detail: ExecuteCloseSuccessful['detail'];
}
export type DecreaseOrderKind = 'TP' | 'SL' | 'Order';
interface DecreaseOrderCreated extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.DecreaseOrderCreated;
  status: HistoryStatus.CREATED;
  detail: {
    positionHash?: string;
    orderHash?: string;
    kind: DecreaseOrderKind;
    triggerPrice?: BigNumber;
    closeQty?: BigNumber;
    broker?: number;
  };
}
interface DecreaseOrderCancelled extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.DecreaseOrderCancelled;
  status: HistoryStatus.CANCELLED;
  detail: {
    positionHash?: string;
    orderHash?: string;
    kind: DecreaseOrderKind;
  };
}
interface DecreaseOrderUpdated extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.DecreaseOrderUpdated;
  status: HistoryStatus.UPDATED;
  detail: {
    positionHash?: string;
    orderHash?: string;
    kind: DecreaseOrderKind;
    oldKind: DecreaseOrderKind;
    oldTriggerPrice?: BigNumber;
    triggerPrice?: BigNumber;
    oldCloseQty?: BigNumber;
    closeQty?: BigNumber;
    broker?: number;
  };
}
export interface UserCloseTradeSuccessful extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful;
  status: HistoryStatus.EXECUTED;
  collateral: BigNumber;
  detail: {
    executionType?: ClosePositionDetailExecutionTypeEnum;
    closePrice: BigNumber;
    fundingFee: BigNumber;
    closeFee: BigNumber;
    holdingFee: BigNumber;
    pnl: BigNumber;
    getPnlUsd: (
      collateralTokenPrice: Nullable<BigNumber>,
    ) => Nullable<BigNumber>;
    pnlAfterFee: BigNumber;
    getPnlAfterFeeUsd: (
      collateralTokenPrice: Nullable<BigNumber>,
    ) => Nullable<BigNumber>;
  };
}
export interface ClosePosition extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.ClosePosition;
  status: HistoryStatus.EXECUTED;
  collateral: BigNumber;
  detail: UserCloseTradeSuccessful['detail'];
}
export interface PositionDecreased extends BaseTradeHistory {
  type: TradeHistoryResponseOperationTypeEnum.PositionDecreased;
  status: HistoryStatus.EXECUTED;
  collateral: BigNumber;
  detail: UserCloseTradeSuccessful['detail'];
}
